import React from 'react'
import PageHead from '../components/Common/PageHead'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHandsClapping, faShield, faSun } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faLinkedin, faYoutube } from '@fortawesome/free-brands-svg-icons';
import { Helmet } from 'react-helmet-async';

const SOCIALS = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/aiagencydirectory?igsh=dWcwdnBoemkybTk4&utm_source=qr',
    icon: faInstagram,
    color: 'bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500'
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/ai-agency-directory/?viewAsMember=true',
    icon: faLinkedin,
    color: 'bg-[#0A66C2]'
  },
  {
    name: 'YouTube',
    url: 'https://youtube.com/@theshahpodcast?si=TqHsa3i1dE732tS3',
    icon: faYoutube,
    color: 'bg-[#FF0000]'
  },
];

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us | AI Agency Directory</title>
        <meta name="description"
          content="Discover leading AI agencies, consultants, and experts in our AI Agency Directory. Explore top AI services, tools, and solutions. Connect and grow today!" />
      </Helmet>

      <div>
        <PageHead pagename='The Company' />
        <div className='flex flex-col md:flex-row items-start justify-between p-4 md:p-32 mx-4 md:mx-16'>
          <h1 className='w-full md:w-1/3 text-secondary text-2xl md:text-3xl font-bold mb-4 md:mb-0'>
            About AI Agency <br /> Directory
          </h1>
          <p className='w-full md:w-2/3 text-base md:text-lg'>
            At AI Agency Directory, we are passionate about connecting businesses with the best AI solutions to drive growth and innovation. Our mission is to serve as a trusted resource in the AI industry, providing businesses with access to top-rated AI agencies and valuable insights to help them thrive in the digital age.
          </p>
        </div>
        <div className='flex items-center justify-center mb-32'>
          <img loading="lazy" src='./assets/comp1.png' />
        </div>
        <div className='flex flex-col md:flex-row items-center justify-between p-4 md:p-32 mx-4 md:mx-16'>
          <div className='space-y-5 w-full md:w-2/3'>
            <h1 className='text-secondary text-2xl md:text-3xl font-bold mb-4 md:mb-0'>
              Our Vision
            </h1>
            <p className='text-base md:text-lg'>
              We envision a future where businesses of all sizes can harness the power of AI to solve complex challenges, improve efficiency, and create meaningful impact in their industries. Through our platform, we aim to democratize access to AI expertise and empower businesses to unlock their full potential.
            </p>
          </div>
          <div className='w-full md:w-1/3'>
            <img loading="lazy" src='./assets/comp2.png' />
          </div>
        </div>

        <div className='p-8 md:p-32 font-medium'>
          <h1 className='text-3xl md:text-5xl font-bold text-secondary text-center my-8 md:my-16'>Our Values</h1>
          <div className='flex flex-col md:flex-row justify-between items-end space-y-12 md:space-y-19 md:space-x-16'>
            <div className='flex flex-col space-y-4 items-center md:items-start text-center md:text-left'>
              <div className='bg-primary p-5 rounded-full flex items-center justify-center w-16 h-16'>
                <FontAwesomeIcon icon={faHandsClapping} className="text-white text-4xl" />
              </div>
              <h2 className='text-secondary text-2xl md:text-3xl font-bold'>Excellence</h2>
              <p>We are committed to excellence in everything we do, from curating top-tier AI agencies to delivering exceptional service to our clients.</p>
            </div>

            <hr className="border-l border-primary h-32 md:h-64 mx-4 hidden md:block" />

            <div className='flex flex-col space-y-4 items-center md:items-start text-center md:text-left'>
              <div className='bg-primary p-5 rounded-full flex items-center justify-center w-16 h-16'>
                <FontAwesomeIcon icon={faSun} className="text-white text-4xl" />
              </div>
              <h2 className='text-secondary text-2xl md:text-3xl font-bold'>Innovation</h2>
              <p>We embrace innovation and continuously seek new ways to leverage AI technology for the benefit of our users and partners.</p>
            </div>

            <hr className="border-l border-primary h-32 md:h-64 mx-4 hidden md:block" />

            <div className='flex flex-col space-y-4 items-center md:items-start text-center md:text-left'>
              <div className='bg-primary p-5 rounded-full flex items-center justify-center w-16 h-16'>
                <FontAwesomeIcon icon={faShield} className="text-white text-4xl" />
              </div>
              <h2 className='text-secondary text-2xl md:text-3xl font-bold'>Integrity</h2>
              <p>We operate with integrity and transparency, building trust with our clients, partners, and stakeholders through honesty and accountability.</p>
            </div>
          </div>
        </div>

        <div className='flex flex-col justify-between items-center font-medium space-y-12 p-8 md:p-32 mb-16 w-full'>
          <h2 className='font-bold text-secondary text-3xl md:text-5xl '>Our Team</h2>
          <p className='w-full md:w-2/3 text-center'>
            Meet the dedicated professionals behind AI Agency Directory who are passionate about helping businesses succeed in the AI landscape. From AI experts to customer support specialists, our team is here to support you every step of the way.
          </p>
          <div className="flex space-x-5 md:space-x-8 mt-2 mb-8">
            {SOCIALS.map((social, idx) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.name}
                className={`rounded-full shadow-lg flex items-center justify-center w-14 h-14 md:w-16 md:h-16 transition-transform hover:scale-105 group ${social.color}`}
              >
                <FontAwesomeIcon
                  icon={social.icon}
                  className="text-white text-2xl md:text-3xl transition-colors group-hover:text-blue-100"
                />
              </a>
            ))}
          </div>
          <a href='/contact'>
            <button className='bg-primary text-white text-lg p-4 px-16 hover:bg-blue-700 rounded-full'>
              Contact Us
            </button>
          </a>
        </div>
      </div>
    </>
  )
}

export default About
